import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Animated from 'react-native-reanimated';
import { validate as isValidUUID } from 'uuid';
import { View } from 'react-native';
import Text from 'design-system/Text';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { AppView, CardView, Indicator, rem, Spacer } from 'design-system';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import { TAB_SCREEN_HEADER_HEIGHT_WEB } from 'navigators/styles';
import { DEFAULT_DELAY_TIME } from 'utils/delay';
import { selectFeatureFlag } from 'reducers/selectors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectIsInvestWalkthroughActive } from 'reducers/selectors/walkthrough';
import InvestWalkthrough from 'features/feed/components/walkthrough/InvestWalkthrough';
import InvestHeaderOverlay from 'features/feed/components/walkthrough/invest/InvestHeaderOverlay';
import useInvestWalkthroughLayout from 'features/feed/components/walkthrough/useInvestWalkthroughLayout';
import CardRowContainer from 'design-system/CardRowContainer';
import FundPickerStockButton from '../components/FundPickerStockButton';
import useFundPickerStore from '../hooks/useFundPickerStore';
import { selectActiveTradingAccountId, selectHasAutoInvest, selectIsTradingAccountAlpaca, selectShowSetupInvestAccountInInvestScreens, } from '../reducers/selectors';
import Section from '../components/Section';
import InfoBlock from '../components/InfoBlock';
import StockChart from '../components/StockChart';
import BuyBlockView from '../components/BuyBlockView';
import SectionHeader from '../components/SectionHeader';
import { addRecentlyViewedAssetIdForTradingAccount, getStockMeta, getStocksMeta } from '../actions';
import AnalystRatings from '../components/AnalystRatings';
import useStockScreenData from '../hooks/useStockScreenData';
import StockScreenHeader from '../components/StockScreenHeader';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import PriceAlertSection from '../components/PriceAlertSection';
import StockAboutSection from '../components/StockAboutSection';
import AnalystPriceTarget from '../components/AnalystPriceTarget';
import StockFinancialSection from '../components/StockFinancialSection';
import StockScreenButtonCTAs from '../components/StockScreenBottomCTAs';
import LimitStopOrdersSection from '../components/LimitStopOrdersSection';
import StockAboutMetricsSection from '../components/StockAboutMetricsSection';
import CorporateEventsSection from '../components/CorporateEventsSection';
import SetupInvestmentAccountButton from '../components/SetupInvestmentAccountButton';
import StockAboutSectionSeccl from '../components/StockAboutSectionSeccl';
import AutoinvestUpsellCard from '../components/AutoinvestUpsellCard';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import DepositAndBuyAssetBuyButton from '../components/DepositAndBuyAssetBuyButton';
import { isWeb } from '../../../constants';
const strings = {
    yourInvestment: 'Your investment',
    activity: 'Activity',
    disclaimer: {
        info1: 'Data displayed above is indicative only and its accuracy or completeness is not guaranteed. Actual execution price may vary.',
        info2: 'Past performance is not an indicative of future results. Capital at risk.',
        info3: 'Data sourced from Refinitiv.',
    },
};
const styleSet = createStyleSheets(() => ({
    scrollView: {
        flex: 1,
    },
    content: {
        paddingBottom: rem(16),
    },
    disclaimer: {
        padding: rem(16),
        paddingVertical: rem(24),
    },
}));
const emptyArr = [];
const StockScreenComponent = withAnimatedNavigationHeader(
// @ts-ignore
({ route, navigation, scrollHandler, scrollY, symbolOrName }) => {
    const assetId = ('assetId' in route.params && route.params.assetId) || ''; // assetId should always be present here
    const recurringOrderFlow = route.params?.recurringOrderFlow;
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const navigateToNextScreen = useNavigateToNextScreen();
    const onboardingState = useAppSelector((state) => state.invest.onboarding);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const isAlpacaAccount = useAppSelector(selectIsTradingAccountAlpaca);
    const hasStockAlerts = useAppSelector((store) => selectFeatureFlag(store, 'stock_alerts').value);
    const hasAutoInvest = useAppSelector(selectHasAutoInvest);
    const showSetupInvestAccountInInvestScreens = useAppSelector(selectShowSetupInvestAccountInInvestScreens);
    const { active: fundPickerActive } = useFundPickerStore();
    const selectedTradingAccountId = useAppSelector((state) => state.unpersistedInvestReducer.selectedTradingAccountId);
    const containerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING);
    const { paddingHorizontalStyle, paddingHorizontal } = useAppFrameDimensions(isWeb);
    // Only the web header lines its title up with the content column
    const webContentInset = isWeb ? paddingHorizontal : undefined;
    const contentContainerStyle = useMemo(() => {
        if (showSetupInvestAccountInInvestScreens || fundPickerActive) {
            return containerStyles;
        }
        return styles.content;
    }, [containerStyles, showSetupInvestAccountInInvestScreens, fundPickerActive, styles.content]);
    const { itemPositions, onLayout, onScreenLayout, screenHeight } = useInvestWalkthroughLayout();
    const [mountEverything, setMountEverything] = useState(false);
    useEffect(() => {
        const timeout = setTimeout(() => setMountEverything(true), 250);
        return () => {
            clearTimeout(timeout);
        };
    }, []);
    const [data] = useStockScreenData(assetId, tradingAccountId, !!isAlpacaAccount);
    useLayoutEffect(() => {
        navigation.setOptions({
            header: () => (<StockScreenHeader assetId={assetId} scrollY={scrollY} hasStockAlerts={hasStockAlerts} webContentInset={webContentInset}/>),
        });
    }, [assetId, hasStockAlerts, isAlpacaAccount, scrollY, webContentInset]);
    const isUserOnboardingInvest = !tradingAccountId && !!onboardingState;
    useAmplitudeOnMountEvent(undefined, 'Stock Screen', { symbol: symbolOrName });
    const scrollViewRef = useRef(null);
    React.useEffect(() => {
        if (selectedTradingAccountId && !isAlpacaAccount) {
            const timeout = setTimeout(() => {
                dispatch(addRecentlyViewedAssetIdForTradingAccount(assetId, selectedTradingAccountId));
            }, DEFAULT_DELAY_TIME);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
    }, [assetId, isAlpacaAccount, selectedTradingAccountId]);
    const activities = useMemo(() => {
        if (!assetId) {
            return [];
        }
        const result = [];
        if (data.activities) {
            for (let i = 0; i < data.activities.length; i += 1) {
                const activity = data.activities[i];
                if (activity.type === 'buy' ||
                    activity.type === 'sell' ||
                    activity.type === 'dividend' ||
                    activity.type === 'switch') {
                    result.push(activity);
                }
            }
        }
        return result;
    }, [assetId, data.activities]);
    const yourInvestment = useMemo(() => {
        const yourInvestmentPosition = data.positions?.[0];
        const position = yourInvestmentPosition;
        if (!position)
            return null;
        return (<>
          <SectionHeader title={strings.yourInvestment}/>
          <CardView noMarginTop noMarginBottom>
            <CardRowContainer>
              <InfoBlock currentValue={position.amount}/>
              <InfoBlock numberOfShares={position.quantity}/>
              {!!position.priceAvg.value && <InfoBlock avgPrice={position.priceAvg}/>}
              <InfoBlock changeAmount={position.unrealizedProfitLoss} changePercent={position.unrealizedProfitLossPct}/>
            </CardRowContainer>
          </CardView>
          {!!hasAutoInvest && <AutoinvestUpsellCard assetId={assetId}/>}
        </>);
    }, [data.positions, hasAutoInvest, assetId]);
    const corporateEventKey = useMemo(() => (data.assetId && isAlpacaAccount ? [data.assetId] : undefined), [data.assetId, isAlpacaAccount]);
    const disclaimer = useMemo(() => (<View style={styles.disclaimer}>
          <Text TextThin-14 Gray centered>
            {strings.disclaimer.info1}
          </Text>
          <Spacer h={10}/>
          <Text TextThin-14 Gray centered>
            {strings.disclaimer.info2}
          </Text>
          <Spacer h={10}/>
          {!!isAlpacaAccount && (<Text TextThin-14 Gray centered>
              {strings.disclaimer.info3}
            </Text>)}
        </View>), [isAlpacaAccount, styles.disclaimer]);
    const queuedOrders = data.queuedOrders ?? emptyArr;
    const pendingOrders = data.pendingOrders ?? emptyArr;
    const news = data.news ?? emptyArr;
    const additionalPadding = useMemo(() => ({
        ...paddingHorizontalStyle,
        paddingHorizontal: (paddingHorizontal || 0) + rem(16),
    }), [paddingHorizontal, paddingHorizontalStyle]);
    const onBuyLayout = useCallback((e) => {
        onLayout('stockScreenBuy', e);
    }, [onLayout]);
    const onStockChartLayout = useCallback((e) => {
        onLayout('stockScreenStockPrice', e);
    }, [onLayout]);
    const handleSelectFund = useCallback(() => {
        dispatch({ type: 'SAVE_ONBOARDING_STATE', assetId });
        navigateToNextScreen();
    }, [assetId, navigateToNextScreen]);
    const BottomButton = useMemo(() => {
        if ('depositAndBuyFlowParams' in route.params && route.params.depositAndBuyFlowParams) {
            return (<DepositAndBuyAssetBuyButton assetId={assetId} amount={route.params.depositAndBuyFlowParams.amount} depositMethod={route.params.depositAndBuyFlowParams.depositMethod} tradingAccountId={route.params.depositAndBuyFlowParams.tradingAccountId}/>);
        }
        if (isUserOnboardingInvest) {
            return <Button brand floating title="Select" onPress={handleSelectFund}/>;
        }
        if (showSetupInvestAccountInInvestScreens) {
            return <SetupInvestmentAccountButton brand floating/>;
        }
        if (fundPickerActive) {
            return <FundPickerStockButton assetId={assetId}/>;
        }
        return (<View onLayout={onBuyLayout}>
          <StockScreenButtonCTAs assetId={assetId} recurringOrderFlow={recurringOrderFlow} canSell={data.positions?.length ? data.positions[0].availableToSell > 0 : false}/>
        </View>);
    }, [
        assetId,
        data.positions,
        fundPickerActive,
        handleSelectFund,
        isUserOnboardingInvest,
        onBuyLayout,
        recurringOrderFlow,
        route.params,
        showSetupInvestAccountInInvestScreens,
    ]);
    if (!assetId) {
        return <AppView />;
    }
    return (<AppView withFrame={false} onLayout={onScreenLayout}>
        <Animated.ScrollView ref={scrollViewRef} style={styles.scrollView} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator={false} contentContainerStyle={contentContainerStyle}>
          <View style={paddingHorizontalStyle} onLayout={onStockChartLayout}>
            <StockChart assetId={assetId} has1DChart={isAlpacaAccount === true}/>
          </View>
          <View style={paddingHorizontalStyle}>
            <BuyBlockView />
          </View>
          {!!isAlpacaAccount && (<Section news={news} assetId={data.assetId} contentContainerStyle={additionalPadding}/>)}
          <View style={paddingHorizontalStyle}>
            {!isAlpacaAccount && !!data.stockMeta?.description && (<>
                <Spacer h={8}/>
                <CardView noMarginTop noMarginBottom withPadding>
                  <Text TextThin-14>{data.stockMeta?.description}</Text>
                </CardView>
              </>)}
            {yourInvestment}
            {mountEverything ? (<>
                {isAlpacaAccount ? (<>
                    <Section orders={queuedOrders} assetId={data.assetId}/>
                    <StockAboutMetricsSection pe={data.stockMeta?.pe} eps={data.stockMeta?.eps} beta={data.stockMeta?.beta} marketCap={data.stockMeta?.marketCap} dividendRate={data.stockMeta?.dividendRate} dividendYield={data.stockMeta?.dividendYield}/>
                    <AnalystRatings assetId={assetId} ratingsTotal={data.analystInfo?.ratingsTotal} buyPercent={data.analystInfo?.buyPercent} holdPercent={data.analystInfo?.holdPercent} sellPercent={data.analystInfo?.sellPercent}/>
                    <AnalystPriceTarget assetId={assetId} currency={data.analystInfo?.currency} ratingsTotal={data.analystInfo?.ratingsTotal} priceTargetLow={data.analystInfo?.priceTargetLow} priceTargetMean={data.analystInfo?.priceTargetMean} priceTargetHigh={data.analystInfo?.priceTargetHigh}/>
                    <StockFinancialSection assetId={data.assetId}/>
                    {!!hasStockAlerts && (<PriceAlertSection assetId={data.assetId} pendingStockAlerts={data.pendingStockAlerts}/>)}
                  </>) : null}
                <Section pending orders={pendingOrders} assetId={data.assetId}/>
                {!!data.limitStopOrders?.length && data.assetId ? (<LimitStopOrdersSection orders={data.limitStopOrders} assetId={data.assetId}/>) : null}
                <Section recurringBuys={data.recurringBuys || emptyArr} hideIfEmpty/>
                <Section activities={activities} assetId={data.assetId}/>

                {!!corporateEventKey && (<CorporateEventsSection assetIds={corporateEventKey} stockName={data.stockMeta?.name}/>)}

                {isAlpacaAccount ? (<StockAboutSection ceo={data.stockMeta?.ceo} scrollViewRef={scrollViewRef} sector={data.stockMeta?.sector} website={data.stockMeta?.website} employees={data.stockMeta?.employees} description={data.stockMeta?.description} headquarters={data.stockMeta?.headquarters}/>) : (<StockAboutSectionSeccl assetId={assetId} fundName={data.stockMeta?.originalName || data.stockMeta?.name} kidUrl={data.stockMeta?.kidUrl} esgUrl={data.stockMeta?.esgUrl} fundRiskLevel={data.stockMeta?.fundRiskLevel} tradingCharge={data.stockMeta?.tradingCharge} ongoingCharge={data.stockMeta?.ongoingCharge} transactionCost={data.stockMeta?.transactionCost} incidentalCost={data.stockMeta?.incidentalCost} platformFee={data.stockMeta?.platformFee} isUserOnboardingInvest={isUserOnboardingInvest}/>)}
              </>) : null}
            {disclaimer}
          </View>
        </Animated.ScrollView>
        {BottomButton}
        <InvestWalkthrough isInTab={false} scrollLayoutHeight={0} scrollHeight={screenHeight} itemPositions={itemPositions}/>
      </AppView>);
}, ({ colors, route }) => ({
    hasCustomTitle: true,
    hideTitleOnScroll: true,
    // On web the header row is 64 tall, so its background must be too
    headerHeight: isWeb ? TAB_SCREEN_HEADER_HEIGHT_WEB : rem(46),
    Overlay: InvestHeaderOverlay,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
    headerTintColor: (route.params.walkthroughInProgress
        ? colors.elements.primaryOnModal
        : colors.text.primary),
}));
const StockScreen = ({ route, navigation }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const params = useMemo(() => {
        if ('assetId' in route.params && route.params.assetId) {
            return { assetId: route.params.assetId };
        }
        const symbolOrAssetId = 'symbolOrAssetId' in route.params ? route.params.symbolOrAssetId : undefined;
        if (isValidUUID(symbolOrAssetId)) {
            return {
                assetId: symbolOrAssetId,
            };
        }
        return {
            symbol: symbolOrAssetId?.toUpperCase(),
        };
    }, [route.params]);
    const assetIdFromCache = useAppSelector((state) => params.symbol ? state.unpersistedInvestReducer.symbolAssetIdMap?.[params.symbol] : undefined);
    const assetId = params.assetId || assetIdFromCache;
    const assetIdFromProps = 'assetId' in route.params ? route.params.assetId : undefined;
    const stockCache = useAppSelector((state) => (assetId ? state.invest.caches?.[assetId] : undefined));
    const symbolFromCache = stockCache?.symbol;
    const nameFromCache = stockCache?.name;
    const walkthroughInProgress = useAppSelector(selectIsInvestWalkthroughActive);
    useLayoutEffect(() => {
        navigation.setParams({
            walkthroughInProgress,
        });
        navigation.setOptions({
            headerTintColor: (walkthroughInProgress ? colors.elements.primaryOnModal : colors.text.primary),
        });
    }, [colors.elements.primaryOnModal, colors.text.primary, walkthroughInProgress]);
    useEffect(() => {
        if (assetId) {
            navigation.setParams({
                assetId,
            });
        }
    }, [assetId]);
    useEffect(() => {
        // we try to fetch assetId from symbol if we have symbol and no assetId
        if (!assetId && params.symbol) {
            dispatch(getStocksMeta([params.symbol]));
        }
    }, [assetId, params.symbol]);
    const hasNoSymbolFromCache = !symbolFromCache;
    useEffect(() => {
        if (assetIdFromProps && hasNoSymbolFromCache) {
            // we try to fetch info from assetId if we have assetId and no symbol
            dispatch(getStockMeta(assetIdFromProps));
        }
    }, [assetIdFromProps, hasNoSymbolFromCache]);
    const symbolOrName = symbolFromCache || nameFromCache;
    if (assetIdFromProps && symbolOrName) {
        // @ts-ignore
        return <StockScreenComponent route={route} navigation={navigation} symbolOrName={symbolOrName}/>;
    }
    return (<AppView type="secondary">
      <Indicator dark/>
    </AppView>);
};
// StockScreen.whyDidYouRender = true;
export default StockScreen;
